import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  Person,
  CalendarToday,
  LocationOn,
  CloudQueue,
  Thermostat,
  Engineering,
  Warning,
  CheckCircle,
  Assignment,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

interface WeatherCondition {
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
}

interface WorkActivity {
  description: string;
  crew: string;
  hoursWorked: number;
  equipment: string[];
  location: string;
}

interface SafetyIncident {
  type: 'incident' | 'near-miss' | 'observation';
  description: string;
  severity: 'low' | 'medium' | 'high';
  actionTaken: string;
  reportedBy: string;
}

interface MaterialDelivery {
  material: string;
  quantity: number;
  unit: string;
  supplier: string;
  deliveryTime: string;
}

interface DailyReportDetail {
  _id: string;
  project: {
    name: string;
    location: string;
  };
  date: string;
  reportedBy: {
    firstName: string;
    lastName: string;
    position: string;
    avatar?: string;
  };
  weather: WeatherCondition;
  workActivities: WorkActivity[];
  safetyIncidents: SafetyIncident[];
  materialDeliveries: MaterialDelivery[];
  totalWorkers: number;
  totalHours: number;
  equipmentUsed: string[];
  delaysOrIssues: string[];
  notes: string;
  photos: string[];
  createdAt: string;
  status: 'draft' | 'submitted' | 'approved';
}

const DailyReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Removed local API base; all network calls use centralized api client

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id) {
          setReport(null);
          return;
        }
        // Guard against invalid MongoDB ObjectId to avoid 400s
        if (!/^[a-fA-F0-9]{24}$/.test(id)) {
          setReport(null);
          throw new Error('Invalid report ID');
        }
        const response = await api.get(`/daily-reports/${id}`);
        const data = response?.data?.data ?? response?.data;
        setReport(data || null);
      } catch (err: any) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message || err?.message || 'Failed to load daily report';
        if (status === 404) {
          setReport(null);
          setError('Daily report not found');
        } else {
          setReport(null);
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'submitted':
        return 'info';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helpers for safely handling variable backend shapes
  const safeArray = (arr: any) => (Array.isArray(arr) ? arr : []);
  const formatTemperature = (temperature: any) => {
    if (typeof temperature === 'number') {
      return `${temperature}°F`;
    }
    if (temperature && typeof temperature === 'object') {
      const unit = temperature.unit === 'celsius' ? '°C' : '°F';
      const high = temperature.high;
      const low = temperature.low;
      if (high != null && low != null) return `H:${high}${unit} L:${low}${unit}`;
      if (high != null) return `${high}${unit}`;
      if (low != null) return `${low}${unit}`;
    }
    return '—';
  };

  // Derived, display-friendly fields
  const activities = safeArray(report?.workProgress?.activities);
  const workActivities = activities.map((activity: any) => ({
    description: activity?.description ?? 'Activity',
    crew: `${safeArray(activity?.crew).length} workers`,
    hoursWorked: safeArray(activity?.crew).reduce((sum: number, m: any) => sum + (m?.hoursWorked || 0), 0),
    equipment: safeArray(activity?.equipment).map((e: any) => e?.name).filter(Boolean),
    location: activity?.location ?? ''
  }));

  const materialDeliveries = activities.flatMap((activity: any) =>
    safeArray(activity?.materials).map((m: any) => ({
      material: m?.name ?? 'Material',
      quantity: m?.quantity ?? 0,
      unit: m?.unit ?? '',
      supplier: m?.supplier ?? '',
      deliveryTime: m?.deliveryTime ? new Date(m.deliveryTime).toLocaleString() : ''
    }))
  );

  const safetyIncidents = safeArray(report?.safety?.incidents).map((i: any) => ({
    type: (i?.type || 'incident') as 'incident' | 'near-miss' | 'observation',
    description: i?.description ?? '',
    severity: (i?.severity || 'low') as 'low' | 'medium' | 'high',
    actionTaken: i?.immediateActions || i?.correctiveAction || '',
    reportedBy: typeof i?.reportedBy === 'string' ? i.reportedBy : '—'
  }));

  const equipmentUsed = activities.flatMap((a: any) => safeArray(a?.equipment).map((e: any) => e?.name)).filter(Boolean);
  const delaysOrIssues = safeArray(report?.issues).map((issue: any) => issue?.description).filter(Boolean);
  const totalWorkers = typeof report?.manpower?.totalWorkers === 'number'
    ? report.manpower.totalWorkers
    : safeArray(report?.manpower?.breakdown).reduce((sum: number, b: any) => sum + (b?.count || 0), 0);
  const totalHours = activities.reduce((sum: number, a: any) => {
    const crewHours = safeArray(a?.crew).reduce((acc: number, m: any) => acc + (m?.hoursWorked || 0), 0);
    const equipHours = safeArray(a?.equipment).reduce((acc: number, e: any) => acc + (e?.hoursUsed || 0), 0);
    return sum + crewHours + equipHours;
  }, 0);
  const temperatureText = formatTemperature(report?.weather?.temperature);
  const humidityText = report?.weather?.humidity != null ? `${report.weather.humidity}%` : '—';
  const wind = report?.weather?.windSpeed;
  const windValue = typeof wind === 'number' ? wind : (wind?.value ?? wind?.speed ?? null);
  const windUnit = (wind?.unit === 'kmh') ? 'km/h' : 'mph';
  const windText = windValue != null ? `${windValue} ${windUnit}` : '—';

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading daily report...</Typography>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">{error ? error : 'Daily report not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/daily-reports')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1 }}>
          Daily Report Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
        </Box>
      </Box>

      {/* Report Info Card */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={report.reportedBy?.avatar || ''}
              sx={{ width: 60, height: 60, mr: 2 }}
            >
              {(report.reportedBy?.firstName?.[0] || '')}{(report.reportedBy?.lastName?.[0] || '')}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {report.project?.name || 'Project'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reported by: {report.reportedBy?.firstName} {report.reportedBy?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {report.project?.location || ''}
              </Typography>
            </Box>
            <Chip
              label={report.status.charAt(0).toUpperCase() + report.status.slice(1)}
              color={getStatusColor(report.status) as any}
              sx={{ mr: 2 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDate(report.date)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Total Workers</Typography>
                <Typography variant="body2" fontWeight="medium">{totalWorkers}</Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Total Hours</Typography>
                <Typography variant="body2" fontWeight="medium">{totalHours} hrs</Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Thermostat fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Temperature</Typography>
                <Typography variant="body2" fontWeight="medium">{temperatureText}</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Work Activities */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Engineering color="primary" />
                Work Activities
              </Typography>
              
              {workActivities.map((activity, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {activity.description}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Crew:</strong> {activity.crew}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Hours:</strong> {activity.hoursWorked}h
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Location:</strong> {activity.location}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    <strong>Equipment:</strong> {safeArray(activity.equipment).join(', ')}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Material Deliveries */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment color="primary" />
                Material Deliveries
              </Typography>
              
              {materialDeliveries.map((delivery, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {delivery.material}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Quantity:</strong> {delivery.quantity} {delivery.unit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Supplier:</strong> {delivery.supplier}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Time:</strong> {delivery.deliveryTime}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Safety Incidents */}
          {safetyIncidents.length > 0 && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning color="warning" />
                  Safety Incidents
                </Typography>
                
                {safetyIncidents.map((incident, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={incident.type.replace('-', ' ').toUpperCase()}
                        size="small"
                        color="warning"
                      />
                      <Chip
                        label={incident.severity.toUpperCase()}
                        size="small"
                        color={getSeverityColor(incident.severity) as any}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      {incident.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      <strong>Action Taken:</strong> {incident.actionTaken}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Reported By:</strong> {incident.reportedBy}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Right Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Weather */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudQueue color="primary" />
                Weather Conditions
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {temperatureText}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {report?.weather?.condition}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Humidity</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {humidityText}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Wind Speed</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {windText}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Equipment Used */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Equipment Used
              </Typography>
              
              <List dense>
                {equipmentUsed.map((equipment, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={equipment}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Delays/Issues */}
          {delaysOrIssues.length > 0 && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'warning.main' }}>
                  Delays & Issues
                </Typography>
                
                <List dense>
                  {delaysOrIssues.map((issue, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Warning color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={issue}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Notes */}
      {report.notes && (
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {report.notes}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DailyReportDetail;